import { Component, computed, EventEmitter, Input, OnInit, Output, Signal } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn
} from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { AppConfigService } from '@core/appconfig.service';
import { School } from '@models/school';
import { CompanyCode } from '@shared/enums/company-code';
import { FormHelpers } from '@shared/form-helpers';
import { Helpers } from '@shared/helpers';
import { instantiateFromJsonReverseParam } from '@shared/zb-object-helper/object-helper';
import { zbpIconNames } from '@shared/zbp-icon/zbp-icon-names';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'zbp-branded-set-school-ids-form',
  templateUrl: './branded-set-school-ids-form.component.html',
  styleUrls: ['./branded-set-school-ids-form.component.scss'],
  standalone: false
})
export class BrandedSetSchoolIdsFormComponent implements OnInit {

  protected readonly zbpIconNames = zbpIconNames;

  form: FormGroup<{
    schools: FormArray<FormGroup<{
      name: FormControl<string>;
      abbreviatedSchoolId: FormControl<string>;
      schoolObject: FormControl<School>;
    }>>
  }>;
  noSchools: boolean = true;
  isZbPortal: boolean = false;

  @Input() set schools(schools: Array<School> | undefined) {
    this.noSchools = !!schools && schools.length === 0;
    this.form.controls.schools = this.buildSchoolsFormArray(schools ?? []);
  }

  @Output() formSave = new EventEmitter<Array<School>>();

  constructor(
    private toastr: ToastrService,
    private formBuilder: FormBuilder,
    private appConfigService: AppConfigService,
    private route: ActivatedRoute,
    private router: Router,
    private titleService: Title,
  ) {
    this.form = new FormGroup({ schools: new FormArray<FormGroup>([]) });
  }

  ngOnInit() {
    this.isZbPortal = this.appConfigService.loginBrand === CompanyCode.ZBPortal;
    this.titleService.setTitle(`Set School ID | ${this.appConfigService.getBrandTitle}`);
  }

  buildSchoolsFormArray(schools: Array<School>): FormArray<FormGroup<{
    name: FormControl<string>;
    abbreviatedSchoolId: FormControl<string>;
    schoolObject: FormControl<School>;
  }>> {
    return this.formBuilder.array(
      schools.map(school => this.formBuilder.group({
        name: [{ value: school.name, disabled: true }],
        abbreviatedSchoolId: this.formBuilder.control(
          school.abbreviatedSchoolId, {
            validators: [
              FormHelpers.getSchoolIdLengthValidator(),
              FormHelpers.getSchoolIdValidator(),
              this.getSchoolIdUniquenessValidator()
            ]
          }),
        schoolObject: [school],
      }))
    );
  }

  get schoolIdMinLength(): number {
    return Helpers.schoolIdMinLength;
  }

  get schoolIdMaxLength(): number {
    return Helpers.schoolIdMaxLength;
  }

  get hasMultipleSchools(): boolean {
    return this.form?.controls.schools.length > 1;
  }

  get brandName(): string {
    switch (this.appConfigService.loginBrand) {
    case CompanyCode.HighlightsPortal:
      return 'Highlights';
    default:
      return 'Zaner-Bloser';
    }
  }

  goBack() {
    this.router.navigate(['..', 'overview'], { relativeTo: this.route });
  }

  private getSchoolIdUniquenessValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      const abbreviatedSchoolId = control.value;
      const count = this.form.controls.schools.controls.reduce((res, c) => (
        c.get('abbreviatedSchoolId').value === abbreviatedSchoolId && abbreviatedSchoolId !== '' ? res + 1 : res
      ), 0);
      return count > 1
        ? { schoolIdNotUnique: 'The school ID must be unique.' }
        : null;
    };
  }

  isInvalid(control: AbstractControl): boolean {
    return control.invalid && (control.dirty || control.touched);
  }

  getErrorMessage(control: AbstractControl): Signal<string> {
    return computed(() => {

      if (!control?.errors) {
        return '';
      }

      const errorMessage = Object.values(control.errors ?? {}).join('<br>');
      return errorMessage;
    });
  }

  onFormSubmit() {
    this.form.updateValueAndValidity();
    if (this.form.valid) {
      const changed: School[] = this.getChangedSchools();
      this.formSave.emit(changed);
    } else {
      const errors = FormHelpers.getAllFormErrors(this.form).reduce((message, error) => (
        `${message}<div>${error}</div>`
      ), '');
      this.toastr.error(errors, '', { enableHtml: true });
    }
  }

  getChangedSchools(): Array<School> {
    return this.form.controls.schools.controls
      .filter(schoolControl => schoolControl.dirty)
      .map((schoolControl) => {
        // Create a new object because school is immutable.
        const schoolObject = instantiateFromJsonReverseParam(School, { ...schoolControl.controls.schoolObject.value });
        schoolObject.abbreviatedSchoolId = schoolControl.controls.abbreviatedSchoolId.value;
        return schoolObject;
      });
  }
}
